var _dec, _dec2, _dec3, _class, _TopNavBarActionItems;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Children, Component } from 'react';
import { omitProps, passthroughProps, safeCloneElement, withDeterministicId } from '@instructure/ui-react-utils';
import { testable } from '@instructure/ui-testable';
import { warn, error } from '@instructure/console';
import { withStyle } from '@instructure/emotion';
import { Drilldown } from '@instructure/ui-drilldown';
import { IconMoreLine } from '@instructure/ui-icons';
import { TruncateList } from '@instructure/ui-truncate-list';
import { TopNavBarItem } from '../TopNavBarItem';
import { mapItemsForDrilldown, renderMappedItemDrilldownSubpages, renderMappedItemsAsDrilldownOptions } from '../utils/mapItemsForDrilldown';
import { TopNavBarContext } from '../TopNavBarContext';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { createElement as _createElement } from "@emotion/react";
/**
---
parent: TopNavBar
id: TopNavBar.ActionItems
---
@module TopNavBarActionItems
**/
let TopNavBarActionItems = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec3 = testable(), _dec(_class = _dec2(_class = _dec3(_class = (_TopNavBarActionItems = class TopNavBarActionItems extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this._hiddenActionItemsMenuId = void 0;
    this._hiddenActionItemsMenuTriggerId = void 0;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.renderOptionContent = (children, itemProps) => {
      const styles = this.props.styles;
      const status = itemProps.status;
      return _jsx("span", {
        css: status === 'active' ? styles === null || styles === void 0 ? void 0 : styles.dropdownMenuOptionActive : styles === null || styles === void 0 ? void 0 : styles.dropdownMenuOption,
        children: children
      });
    };
    this._hiddenActionItemsMenuId = props.deterministicId('TopNavBarActionItems-hiddenActionItemsMenu');
    this._hiddenActionItemsMenuTriggerId = props.deterministicId('TopNavBarActionItems-hiddenActionItemsMenuTrigger');
    this.state = {
      key: 0,
      visibleActionItemsCount: void 0
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, this.makeStylesVariables);
  }
  componentDidUpdate(prevProps) {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, this.makeStylesVariables);
    if (this.context.layout === 'smallViewport' && Children.count(prevProps.children) !== Children.count(this.props.children)) {
      this.setState({
        key: this.state.key + 1
      });
    }
  }
  get makeStylesVariables() {
    return {
      layout: this.context.layout
    };
  }
  get childrenArray() {
    const children = Children.toArray(this.props.children);
    return children.map(child => {
      if (!(child !== null && child !== void 0 && child.props)) {
        return null;
      }
      const _child$props = child.props,
        id = _child$props.id,
        renderAvatar = _child$props.renderAvatar,
        renderIcon = _child$props.renderIcon,
        variant = _child$props.variant;
      if (renderAvatar) {
        warn(false, `Items in <TopNavBar.ActionItems> are not allowed to have avatars, please remove it from item with the id "${id}".`);
        return safeCloneElement(child, {
          renderAvatar: void 0,
          variant: 'default'
        });
      }
      if (this.context.layout === 'smallViewport' && variant !== 'icon') {
        if (!renderIcon) {
          error(false, `Items in <TopNavBar.ActionItems> are required to have the \`renderIcon\` prop, because only the icons are displayed due to the lack of space. Please add an icon to the item with the id "${id}".`);
          return null;
        }
        return safeCloneElement(child, {
          variant: variant === 'forceIconWithLabel' ? 'default' : 'icon'
        });
      }
      return child;
    });
  }
  renderHiddenActionItemsMenu(hiddenItems) {
    const _this$props3 = this.props,
      renderHiddenItemsMenuTriggerLabel = _this$props3.renderHiddenItemsMenuTriggerLabel,
      renderHiddenItemsMenuTriggerTooltip = _this$props3.renderHiddenItemsMenuTriggerTooltip;
    const mappedItems = mapItemsForDrilldown(hiddenItems, {
      renderOptionContent: this.renderOptionContent
    });
    const subPages = renderMappedItemDrilldownSubpages(mappedItems);
    const options = renderMappedItemsAsDrilldownOptions(mappedItems);
    const label = typeof renderHiddenItemsMenuTriggerLabel === 'function' ? renderHiddenItemsMenuTriggerLabel(hiddenItems.length) : renderHiddenItemsMenuTriggerLabel;
    const tooltip = typeof renderHiddenItemsMenuTriggerTooltip === 'function' ? renderHiddenItemsMenuTriggerTooltip(hiddenItems.length) : renderHiddenItemsMenuTriggerTooltip;
    return _jsx(TopNavBarItem, {
      id: this._hiddenActionItemsMenuTriggerId,
      renderIcon: IconMoreLine,
      variant: "icon",
      tooltip: tooltip,
      showSubmenuChevron: false,
      renderSubmenu: _jsx(Drilldown, {
        shouldSetAriaExpanded: false,
        rootPageId: this._hiddenActionItemsMenuId,
        children: [_jsx(Drilldown.Page, {
          id: this._hiddenActionItemsMenuId,
          children: options
        }, this._hiddenActionItemsMenuId), ...subPages]
      }),
      children: label
    });
  }
  renderTruncatedActionItemList() {
    const _this$props4 = this.props,
      listLabel = _this$props4.listLabel,
      styles = _this$props4.styles;
    return _createElement(TruncateList, {
      ...passthroughProps(omitProps(this.props, allowedProps)),
      key: this.state.key // rerender if child count changes
      ,
      elementRef: this.handleRef,
      css: styles === null || styles === void 0 ? void 0 : styles.topNavBarActionItems,
      visibleItemsCount: this.state.visibleActionItemsCount,
      onUpdate: ({
        visibleItemsCount
      }) => {
        this.setState({
          visibleActionItemsCount: visibleItemsCount
        });
      },
      renderHiddenItemMenu: hiddenChildren => this.renderHiddenActionItemsMenu(hiddenChildren),
      "aria-label": listLabel
    }, this.childrenArray);
  }
  render() {
    const _this$props5 = this.props,
      listLabel = _this$props5.listLabel,
      styles = _this$props5.styles;
    if (!this.childrenArray.length) {
      return null;
    }
    if (this.context.layout === 'smallViewport') {
      return this.renderTruncatedActionItemList();
    }
    return _jsx("ul", {
      ...omitProps(this.props, allowedProps),
      ref: this.handleRef,
      css: styles === null || styles === void 0 ? void 0 : styles.topNavBarActionItems,
      "aria-label": listLabel,
      children: this.childrenArray.map(item => _jsx("li", {
        css: styles === null || styles === void 0 ? void 0 : styles.listItem,
        children: item
      }, item.props.id))
    });
  }
}, _TopNavBarActionItems.displayName = "TopNavBarActionItems", _TopNavBarActionItems.componentId = 'TopNavBar.ActionItems', _TopNavBarActionItems.propTypes = propTypes, _TopNavBarActionItems.allowedProps = allowedProps, _TopNavBarActionItems.defaultProps = {}, _TopNavBarActionItems.contextType = TopNavBarContext, _TopNavBarActionItems)) || _class) || _class) || _class);
export { TopNavBarActionItems };
export default TopNavBarActionItems;